import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { generatePath, useNavigate } from "react-router-dom"
import { Button, Stack } from "@mui/material"
import { useFormikContext } from "formik"
import { MRT_RowSelectionState } from "material-react-table"
import { closeSnackbar, enqueueSnackbar } from "notistack"
import { faTrashCan, faUserPlus } from "@fortawesome/pro-light-svg-icons"

import { ErrorDisplay } from "../../../../../../core/api/errorTypes"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { COMPANY_ADMIN_ORGANIZATION_USERS_EDIT_ROUTE } from "../../../../../../constants/routes"
import { UserDetailsProps } from "../../../../../../shared/components/createOrder/orderOnBehalf/OrderOnBehalfFlow"
import { OrderOnBehalfScope } from "../../../../../../shared/components/createOrder/orderOnBehalf/orderOnBehalfTypes"
import { UserSelector } from "../../../../../../shared/components/createOrder/orderOnBehalf/UserSelector"
import { ConfirmModal } from "../../../../../../shared/components/dialogs/ConfirmModal"
import { EmptyContent } from "../../../../../../shared/components/skeleton/EmptyContent"
import { ActionBarItem, FloatingActionBar } from "../../../../../../shared/components/table/FloatingActionBar"
import { useCurrentCompany } from "../../../../hooks/useCurrentCompany"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { UserType } from "../../../../../login/loginTypes"
import { tailwindPalette } from "../../../../../theme/tailwindPalette"
import { CompanyUserDetailsDrawer } from "../../users/components/CompanyUserDetailsDrawer"
import { CompanyUserDetailsType } from "../../users/companyUserDetailsTypes"
import { CompaniesUsersResponse, CompanyAdminUsersQuery, fetchCompaniesUsers } from "../../users/usersApi"

import { toWorkgroupMemberRow } from "../workgroupMembersLib"
import { WorkgroupFormValues, WorkgroupMember, WorkgroupMemberRow } from "../workgroupsTypes"
import { ALL_DEPARTMENTS, NO_DEPARTMENT } from "./SelectDepartmentDropdown"
import { WorkgroupMembersTable } from "./WorkgroupMembersTable"

const VISIBLE_REMOVAL_NAMES_LIMIT = 3

const matchesDepartment = (member: WorkgroupMemberRow, department: string) => {
    if (department === ALL_DEPARTMENTS) return true
    if (department === NO_DEPARTMENT) return !member.department

    return member.department?.name === department
}

const selectUsersById = (data: CompaniesUsersResponse) => new Map(data.users.map((user) => [user.id, user]))

export function WorkgroupMembersPanel() {
    const { l10n } = useAppLocalization()
    const navigate = useNavigate()
    const { companyId } = useCurrentCompany()
    const { values, setFieldValue } = useFormikContext<WorkgroupFormValues>()

    const [userDetails, setUserDetails] = useState<UserDetailsProps>({})
    const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({})
    const [department, setDepartment] = useState(ALL_DEPARTMENTS)
    const [pendingRemoval, setPendingRemoval] = useState<ReadonlyArray<WorkgroupMemberRow>>([])
    const [detailsMember, setDetailsMember] = useState<WorkgroupMemberRow | null>(null)

    const membersDetails = useApiQuery(fetchCompaniesUsers, {
        queryName: CompanyAdminUsersQuery.USERS_FOR_COMPANY,
        dependencies: { companyId },
        errorDisplay: ErrorDisplay.NONE,
        select: selectUsersById,
    })

    const members = values.members
    const membersRef = useRef(members)
    membersRef.current = members
    const memberIds = useMemo(() => members.map((member) => member.id), [members])
    const rows = useMemo(
        () => members.map((member) => toWorkgroupMemberRow(member, membersDetails.data?.get(member.id))),
        [members, membersDetails.data],
    )
    const departments = useMemo(
        () =>
            Array.from(new Set(rows.flatMap((row) => (row.department?.name ? [row.department.name] : [])))).sort(
                (a, b) => a.localeCompare(b),
            ),
        [rows],
    )
    const visibleRows = useMemo(() => rows.filter((row) => matchesDepartment(row, department)), [rows, department])
    const selectedRows = useMemo(
        () => visibleRows.filter((row) => rowSelection[String(row.id)]),
        [visibleRows, rowSelection],
    )

    const scope = useMemo<OrderOnBehalfScope>(() => ({ type: UserType.COMPANY, companyId }), [companyId])
    const { selectedUserId, selectedUserName } = userDetails

    useEffect(() => {
        if (!selectedUserId || !selectedUserName) return

        setFieldValue("members", [{ id: selectedUserId, displayName: selectedUserName }, ...members])
        setUserDetails({})
    }, [selectedUserId, selectedUserName, members, setFieldValue])

    const removeMembers = useCallback(
        (removed: ReadonlyArray<WorkgroupMember>) => {
            const removedIds = removed.map((member) => member.id)

            setFieldValue(
                "members",
                members.filter((member) => !removedIds.includes(member.id)),
            )
            setRowSelection({})

            const undoLabel = l10n.getString("create-or-edit-company-workgroup-members-undo-button")

            const restore = () =>
                setFieldValue("members", [
                    ...removed.filter((member) => !membersRef.current.some((item) => item.id === member.id)),
                    ...membersRef.current,
                ])

            enqueueSnackbar(
                l10n.getString("create-or-edit-company-workgroup-members-removed", { count: removed.length }),
                {
                    variant: "success",
                    action: (key) => (
                        <Button
                            color="inherit"
                            onClick={() => {
                                restore()
                                closeSnackbar(key)
                            }}
                        >
                            {undoLabel}
                        </Button>
                    ),
                },
            )
        },
        [members, setFieldValue, l10n],
    )

    const handleRemove = useCallback((member: WorkgroupMemberRow) => removeMembers([member]), [removeMembers])

    const removalNames = pendingRemoval
        .slice(0, VISIBLE_REMOVAL_NAMES_LIMIT)
        .map((member) => member.displayName)
        .join("; ")
    const hiddenRemovalCount = Math.max(pendingRemoval.length - VISIBLE_REMOVAL_NAMES_LIMIT, 0)

    return (
        <Stack spacing="1.5rem">
            <UserSelector
                scope={scope}
                userDetails={userDetails}
                setUserDetails={setUserDetails}
                labelTranslationKey="create-or-edit-company-workgroup-members-add-field"
                excludedUserIds={memberIds}
            />

            {rows.length > 0 ? (
                <WorkgroupMembersTable
                    members={visibleRows}
                    departments={departments}
                    department={department}
                    onDepartmentChange={setDepartment}
                    isLoading={membersDetails.isInitialLoading}
                    isFetching={membersDetails.isFetching}
                    isError={membersDetails.isError}
                    rowSelection={rowSelection}
                    onRowSelectionChange={setRowSelection}
                    onRemove={handleRemove}
                    onShowDetails={setDetailsMember}
                />
            ) : (
                <LocalizedStrict
                    id="create-or-edit-company-workgroup-members-empty"
                    attrs={{ title: true, description: true }}
                >
                    <EmptyContent
                        icon={faUserPlus}
                        backgroundColor={tailwindPalette.slate[400]}
                        title="No members yet"
                        description="Add the people who should share the mailbox and see each other's bookings."
                    />
                </LocalizedStrict>
            )}

            {selectedRows.length > 0 && (
                <FloatingActionBar
                    selectedItemsLength={selectedRows.length}
                    handleCloseActionBar={() => setRowSelection({})}
                >
                    <LocalizedStrict id="create-or-edit-company-workgroup-members-bulk-remove" attrs={{ title: true }}>
                        <ActionBarItem
                            icon={faTrashCan}
                            title="Remove from group"
                            onClick={() => setPendingRemoval(selectedRows)}
                        />
                    </LocalizedStrict>
                </FloatingActionBar>
            )}

            <LocalizedStrict
                id="create-or-edit-company-workgroup-members-remove-dialog"
                attrs={{ buttonLabelSubmit: true, buttonLabelCancel: true }}
                vars={{ count: pendingRemoval.length }}
            >
                <ConfirmModal
                    open={pendingRemoval.length > 0}
                    onClose={() => setPendingRemoval([])}
                    title={
                        <LocalizedStrict
                            id="create-or-edit-company-workgroup-members-remove-dialog-title"
                            vars={{ count: pendingRemoval.length }}
                        >
                            <span>Remove {pendingRemoval.length} members from the group?</span>
                        </LocalizedStrict>
                    }
                    message={
                        values.name ? (
                            <LocalizedStrict
                                id="create-or-edit-company-workgroup-members-remove-dialog-message"
                                vars={{ names: removalNames, hidden: hiddenRemovalCount, workgroup: values.name }}
                            >
                                <span>
                                    {removalNames} and {hiddenRemovalCount} more will be removed from {values.name} and
                                    will no longer see the group's appointments. The users will not be deleted.
                                </span>
                            </LocalizedStrict>
                        ) : (
                            <LocalizedStrict
                                id="create-or-edit-company-workgroup-members-remove-dialog-message-unnamed"
                                vars={{ names: removalNames, hidden: hiddenRemovalCount }}
                            >
                                <span>
                                    {removalNames} and {hiddenRemovalCount} more will be removed from this group and
                                    will no longer see the group's appointments. The users will not be deleted.
                                </span>
                            </LocalizedStrict>
                        )
                    }
                    handleClick={() => removeMembers(pendingRemoval)}
                    buttonLabelSubmit="Remove members"
                    buttonLabelCancel="Cancel"
                />
            </LocalizedStrict>

            <CompanyUserDetailsDrawer
                userId={detailsMember?.id ?? 0}
                detailsType={CompanyUserDetailsType.USER}
                isActive={detailsMember?.isActive ?? true}
                open={Boolean(detailsMember)}
                onClose={() => setDetailsMember(null)}
                onEdit={() => {
                    if (!detailsMember) return
                    navigate(
                        generatePath(COMPANY_ADMIN_ORGANIZATION_USERS_EDIT_ROUTE, { userId: String(detailsMember.id) }),
                    )
                }}
            />
        </Stack>
    )
}
